import React from "react";
import PropTypes from "prop-types";
import { controlPropTypes } from "../../formhelpers";
import ActionButton from "../ActionButton";
import HeaderCell from "./HeaderCell";
import HybridCell from "./HybridCell";
import formatId from "lib/formatId";
import ErrorBoundary from "lib/common/components/ErrorBoundary";
import invariant from "invariant";
import EntityName from "data/util/EntityName";

export const ADD_NONE = null;
export const ADD_EACH_ROW = "each-row";
export const ADD_LAST_ROW = "last-row";
export const ADD_TOP = "top";
export const ADD_TOP_EACH = "top-each";
export const ADD_TOP_LAST = "top-last";
export const ADD_BOTTOM_BUTTON = "bottom-button";

export default class RecordGrid extends React.Component {
    getRowActions() {
        const {allowView, addMode, allowCreate, allowRemove} = this.props;
        const rowActions = [];
        if(allowView) {
            rowActions.push("view");
        }
        if(allowCreate && [ADD_EACH_ROW, ADD_LAST_ROW, ADD_TOP_EACH, ADD_TOP_LAST].includes(addMode)) {
            rowActions.push("add");
        }
        if(allowRemove) {
            rowActions.push("remove");
        }
        return rowActions;
    }

    getCreatableOptions() {
        const {creatables} = this.props;
        return creatables.map((c) => ({value: c.id, ...c}));
    }

    renderTopActions() {
        const {addMode, descriptor, allowCreate, onAddClick, onFocus, onBlur, onKeyDown, enabled} = this.props;
        if(allowCreate && [ADD_TOP, ADD_TOP_EACH, ADD_TOP_LAST].includes(addMode)) {
            return <ActionButton
                id={descriptor.id + "-add-top"}
                actionType="new"
                label="Add New"
                dataAlign="right"
                onClick={(e, d, v) => onAddClick && onAddClick(e, descriptor, v)}
                options={this.getCreatableOptions()}
                onKeyDown={onKeyDown}
                onFocus={onFocus}
                onBlur={onBlur}
                enabled={enabled}
            />;
        }
        return null;
    }

    renderRowActions(record, index) {
        const {addMode, records, descriptor, onAddClick, onRemoveClick, onViewClick, onFocus, onBlur, onKeyDown, enabled} = this.props;
        const actions = [];
        const unique = descriptor.id+record.id+index;
        const rowActions = this.getRowActions();
        const creatableOptions = this.getCreatableOptions();
        rowActions.forEach((actionType) => {
            const actionProps = {
                id: unique + actionType,
                key: unique + actionType,
                actionType: actionType,
                onFocus: onFocus,
                onBlur: onBlur,
                onKeyDown: onKeyDown,
                enabled: enabled
            };
            switch(actionType) {
                case "view":
                    actionProps.onClick = (e) => onViewClick && onViewClick(e, descriptor, record, index);
                    actionProps.entityKey = record.entityKey;
                    break;
                case "add":
                    if(index !== (records.length-1) && [ADD_LAST_ROW, ADD_TOP_LAST].includes(addMode)) {
                        // only want "add" on the last row.
                        return;
                    }
                    if(creatableOptions.length === 1) {
                        // add action
                        actionProps.onClick = (e, d, v) =>
                            onAddClick && onAddClick(e, descriptor, v, record);
                        actionProps.options = creatableOptions;
                    }
                break;
                case "remove":
                    actionProps.onClick = (e) => onRemoveClick && onRemoveClick(e, descriptor, record, index);
                    actionProps.entityKey = record.entityKey;
                    break;
                default:
                    return;

            }
            actions.push(<ActionButton {...actionProps} />);
        });
        return actions;
    }

    renderBottomActions() {
        const {addMode, descriptor, creatables, allowCreate, onAddClick, onFocus, onBlur, onKeyDown, enabled} = this.props;
        if(allowCreate && [ADD_BOTTOM_BUTTON].includes(addMode)) {
            const actions = [];
            creatables.forEach((c,index) => {
                invariant(EntityName.is(c.id), `non-entityKey creatable id: '${c.id}'`);
                actions.push(
                    <ActionButton
                        key={descriptor.id+index}
                        id={descriptor.id + "-add-bottom-" + index}
                        actionType="new"
                        label={"Add New " + c.label}
                        onClick={(e, d, v) =>
                            onAddClick && onAddClick(e, descriptor, c)}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onKeyDown={onKeyDown}
                        enabled={enabled}
                        entityKey={c.id}
                    />
                );
            });
            return actions;
        }
        return null;
    }

    render() {
        const {columns, records, onHeaderClick, onViewClick, allowView, descriptor} = this.props;
        const rowActions = this.getRowActions();
        const actionWidth = rowActions.length * 22;
        const topActions = this.renderTopActions();
        const hasActionColumn = (topActions !== null) || (rowActions.length > 0);
        return <ErrorBoundary>
            <div key={descriptor.id} className="record-grid-container">
                <div className="record-grid">
                    <table className="table-alt table-condensed">
                        <thead>
                        <tr>
                            {columns.map((column, index) => <HeaderCell
                                key={index+column.id}
                                onClick={(e) => onHeaderClick && onHeaderClick(e, column)}
                                column={column}
                            />)}
                            {hasActionColumn && <th className="actions" width={actionWidth}>
                                <div className="btn-group">
                                    {topActions}
                                </div>
                            </th>}
                        </tr>
                        </thead>
                        <tbody>
                        {records.map((record, recordIndex) => (
                            <tr key={record.id} id={formatId(record.id, "grid-row")}>
                                {columns.map((column, columnIndex) => (
                                    <HybridCell
                                        key={columnIndex + column.id}
                                        onDoubleClick={(e) => (allowView && onViewClick) && onViewClick(e, descriptor, record, column)}
                                        record={record}
                                        column={column}
                                    />
                                ))}
                                {hasActionColumn && <td className="actions" width={actionWidth}>
                                    <p className="form-control-static">
                                        {this.renderRowActions(record, recordIndex)}
                                    </p>
                                </td>}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
                <div className="bottom-button-row-container">
                    <div className="bottom-button-row">
                        {this.renderBottomActions()}
                    </div>
                </div>
            </div>
        </ErrorBoundary>;

    }
}

RecordGrid.defaultProps = {
    addMode: ADD_TOP,
    allowView: false,
    allowCreate: false,
    allowRemove: false,
    singularLabel: "Record",
    records: [],
    columns: [],
    creatables: [],
};

RecordGrid.propTypes = {
    ...controlPropTypes(null, false),
    onCommitChange: PropTypes.func, // not required

    addMode: PropTypes.oneOf([
        ADD_NONE,
        ADD_EACH_ROW,
        ADD_LAST_ROW,
        ADD_TOP,
        ADD_TOP_EACH,
        ADD_TOP_LAST,
        ADD_BOTTOM_BUTTON
    ]),
    allowView: PropTypes.bool,
    allowCreate: PropTypes.bool,
    allowRemove: PropTypes.bool,

    columns: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        content: PropTypes.func.isRequired, /* column render function given the record data */
        label: PropTypes.any,
        width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })).isRequired,
    records: PropTypes.arrayOf(PropTypes.object).isRequired,
    creatables: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.any.isRequired
    })).isRequired,

    // actions
    onAddClick: PropTypes.func,
    onRemoveClick: PropTypes.func,
    onHeaderClick: PropTypes.func,
    onViewClick: PropTypes.func,
};



// WEBPACK FOOTER //
// ./app/lib/common/descriptorcontrols/BaseControls/RecordGrid/RecordGrid.js